import * as THREE from 'three'
import { ensureRaycastAccelerationForObjectTree } from '../../raycast-acceleration/ensureRaycastAccelerationForObjectTree'

/** 同一父对象的 children 顺序变化时派发的轻量场景结构事件。 */
export const SCENE_OBJECT_CHILD_ORDER_CHANGED_EVENT = 'puppy-child-order-changed'

/**
 * 规范化子节点插入位置。
 *
 * @param childCount 父节点当前子节点数量。
 * @param index 期望插入位置。
 * @returns 归一化后的插入索引；未指定时返回 `null`。
 */
function normalizeChildIndex(childCount: number, index?: number): number | null {
  if (!Number.isInteger(index)) {
    return null
  }

  const resolvedIndex = Math.max(0, Math.min(index, childCount))
  return resolvedIndex
}

/**
 * 在不破坏 `Object3D.add()` 事件语义的前提下重排子节点顺序。
 *
 * @param parent 父节点。
 * @param object 目标对象。
 * @param targetIndex 目标插入位置。
 * @returns 对象确实位于该父节点下且顺序发生变化时返回 `true`；未挂载或位置未变时返回 `false`。
 */
export function moveObject3DChildToIndex(
  parent: THREE.Object3D,
  object: THREE.Object3D,
  targetIndex: number,
): boolean {
  const currentIndex = parent.children.indexOf(object)
  if (currentIndex < 0) {
    return false
  }

  const boundedIndex = Math.max(0, Math.min(targetIndex, parent.children.length - 1))
  if (currentIndex === boundedIndex) return false

  parent.children.splice(currentIndex, 1)
  parent.children.splice(boundedIndex, 0, object)
  /** Three.js 不为同父级重排派发事件；显式通知索引仅在结构变化后校验顺序。 */
  parent.dispatchEvent({ type: SCENE_OBJECT_CHILD_ORDER_CHANGED_EVENT } as never)
  return true
}

/**
 * 执行不包含射线加速决策的统一挂载与顺序维护。
 *
 * @param object 待挂载对象。
 * @param parent 父节点。
 * @param index 可选插入位置。
 * @returns 已挂载对象。
 */
function mountObject3DToParent<TObject extends THREE.Object3D>(
  object: TObject,
  parent: THREE.Object3D,
  index?: number,
): TObject {
  if (object.parent !== parent) {
    parent.add(object)
  }

  const normalizedIndex = normalizeChildIndex(parent.children.length, index)
  if (normalizedIndex !== null) {
    moveObject3DChildToIndex(parent, object, normalizedIndex)
  }

  return object
}

/**
 * 将对象挂到指定父节点下，并在挂载前构建整棵子树的 BVH。
 *
 * 设计说明：
 * 1. 这是真正的低层场景入场函数，所有“对象进入已存在场景树”的路径都应收口到这里；
 * 2. BVH 构建发生在挂载前，保证对象一进入场景就具备高性能 raycast 能力；
 * 3. `index` 通过“先 add 再重排 children”实现，既复用 three.js 的事件派发，又支持稳定插入顺序。
 *
 * @param object 待挂载对象。
 * @param parent 父节点。
 * @param index 可选插入位置。
 * @returns 已挂载对象。
 */
export function addObject3DToParent<TObject extends THREE.Object3D>(
  object: TObject,
  parent: THREE.Object3D,
  index?: number,
): TObject {
  ensureRaycastAccelerationForObjectTree(object)
  return mountObject3DToParent(object, parent, index)
}

/**
 * 挂载明确不可选择的纯视觉对象，不为其临时几何构建拾取 BVH。
 *
 * 该入口只允许已经声明 `skipSelection` 或 `skipSelectionSubtree` 的对象使用；
 * 视觉 overlay、包围盒等对象不会进入任何射线候选，为其建立 BVH 只会增加 Hover 热路径成本。
 * 其它业务对象仍必须使用 {@link addObject3DToParent}，避免漏掉合法拾取加速。
 *
 * @param object 已声明不可选择的纯视觉对象。
 * @param parent 父节点。
 * @param index 可选插入位置。
 * @returns 已挂载对象。
 */
export function addNonSelectableObject3DToParent<TObject extends THREE.Object3D>(
  object: TObject,
  parent: THREE.Object3D,
  index?: number,
): TObject {
  if (object.userData?.skipSelection !== true && object.userData?.skipSelectionSubtree !== true) {
    throw new Error('不可选择对象挂载前必须声明 skipSelection 或 skipSelectionSubtree')
  }
  return mountObject3DToParent(object, parent, index)
}
